import React, { useCallback, useEffect, useState } from 'react';
import { getIcon } from '@ohif/ui';
import { MailIcon, LockIcon, ClinicIcon, EyeIcon, EyeOffIcon } from './authIcons';
import { AppDispatch, RootState } from '../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import {
  loginApi,
  Payload as LoginPayload,
  saveDeviceToken,
} from '../../rtk-apis/auth/login';
import { verifyLoginMfaApi } from '../../rtk-apis/auth/verifyLoginMfa';
import { capitalizeFirstLetterOfEachWord } from '../../Utils/utils';
import { Formik, Form } from 'formik';
import { isValidUser } from '../../Utils/authUtils';
import { loginInitialValues, loginValidationSchema } from './helper/Schema';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Failed } from '../../components';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Autocomplete,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Switch,
  RadioGroup,
  Radio,
  Button,
  Link,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import { keyframes } from '@mui/system';
import { clearkcDcmTokenState } from '../../store/slices/Auth/kc-dcm-token';
import { multipleHospitalsApi } from '../../rtk-apis/auth/multipleHospitals';
import { Turnstile } from 'react-turnstile';
import { shouldUseSonographerFirstLogin } from '../../Utils/loginRolePreference';

// ── Theme tokens (navy + teal, matches the studies/report redesign) ──────────
const ekg = keyframes`
  to { stroke-dashoffset: 0; }
`;
const drawRing = keyframes`
  to { stroke-dashoffset: 0; }
`;
const drawCheck = keyframes`
  to { stroke-dashoffset: 0; }
`;
const fieldSx = {
  mb: 1.75,
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#0b1726',
    borderRadius: '12px',
    color: '#e7eef7',
    '& fieldset': { borderColor: '#243652' },
    '&:hover fieldset': { borderColor: '#314a6b' },
    '&.Mui-focused fieldset': {
      borderColor: '#2dd4bf',
      boxShadow: '0 0 0 3px rgba(45,212,191,.15)',
    },
  },
  '& .MuiInputBase-input': { color: '#e7eef7' },
  '& input::placeholder': { color: '#6a7e9c', opacity: 1 },
  '& .MuiFormHelperText-root': { color: '#fb7185', marginLeft: '2px' },
  '& .MuiSelect-icon': { color: '#6a7e9c' },
  '& svg': { color: '#6a7e9c' },
};
const btnSx = {
  mt: 2,
  height: 50,
  borderRadius: '12px',
  textTransform: 'none',
  fontSize: 15,
  fontWeight: 700,
  color: '#04221d',
  background: 'linear-gradient(90deg,#14b8a6,#2dd4bf)',
  boxShadow: '0 8px 24px -8px rgba(45,212,191,.6)',
  '&:hover': { background: 'linear-gradient(90deg,#0f9e8e,#26c2ad)' },
};
const linkSx = {
  color: '#2dd4bf',
  fontSize: 13,
  textDecoration: 'none',
  fontFamily: 'inherit',
  cursor: 'pointer',
  '&:hover': { color: '#5eead4', textDecoration: 'underline' },
};
const switchSx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: '#2dd4bf' },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#2dd4bf' },
};

const radioSx = { color: '#6a7e9c', '&.Mui-checked': { color: '#2dd4bf' } };

const Login = () => {
  const dispatch = useDispatch<AppDispatch>();
  const loginResponse = useSelector((state: RootState) => state.loginResponse);
  const kcDcmTokenResponse = useSelector((state: RootState) => state.kcDcmTokenResponse);
  const multipleHospitalsResponse = useSelector(
    (state: RootState) => state.multipleHospitalsResponse
  );
  const [showPassword, setShowPassword] = useState(false);
  // Login MFA (email one-time code) — /login returns { mfaRequired: true,
  // challengeId, expiresAt, email } instead of tokens when the account needs
  // this step; the actual sign-in only completes once /verify-login-mfa
  // exchanges the emailed code for the real tokens.
  const [mfaChallenge, setMfaChallenge] = useState<{
    challengeId: string;
    maskedEmail: string;
    expiresAt: string;
  } | null>(null);
  const [otp, setOtp] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpSubmitting, setOtpSubmitting] = useState(false);
  const [displayMessage, setDisplayMessage] = useState('');
  const [apiRejectedReponse, setApiRejectedReponse] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFailedModal, setShowFailedModal] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileError, setTurnstileError] = useState<string | null>(null);
  // Turnstile tokens are single-use (~5 min expiry). Bumping this key remounts
  // the widget so a retry after a failed sign-in / expiry gets a FRESH token
  // instead of resubmitting a dead one ("Security check failed" loop).
  const [turnstileKey, setTurnstileKey] = useState(0);
  const resetTurnstile = () => {
    setTurnstileToken(null);
    setTurnstileKey(k => k + 1);
  };
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromValue = searchParams.get('from'); // "email" in this case
  const [selectedHospital, setSelectedHospital] = useState('');
  const [loginMode, setLoginMode] = useState<'clinician' | 'gp'>('clinician');

  const hospitalsWithRoles =
    multipleHospitalsResponse?.hospitals?.map(hospital => ({
      ...hospital,
      userRole: hospital.userRole,
    })) || [];

  useEffect(() => {
    if (multipleHospitalsResponse?.hospitals?.length === 1) {
      setSelectedHospital(multipleHospitalsResponse.hospitals[0]._id);
    }
  }, [multipleHospitalsResponse]);

  const submitLogin = async (
    values: { email: string; password: string; rememberMe: boolean },
    { setSubmitting }: any
  ) => {
    // Enforce captcha in UI as well for clearer UX
    if (!turnstileToken) {
      setTurnstileError('Please complete the security check before signing in.');
      setSubmitting(false);
      return;
    }

    const payload: LoginPayload = {
      email: values.email.toLowerCase(),
      password: values.password,
      turnstileToken,
    };

    const preferSonographer = shouldUseSonographerFirstLogin(hospitalsWithRoles, selectedHospital);

    // For a single-clinic Sonographer entry, omitting hospitalId lets the existing
    // auth role priority choose Sonographer before the matching Admin account.
    if (!preferSonographer) {
      // 🟢 If only one hospital is returned → auto-use it
      if (
        (loginResponse?.result?.data?.hospitalId || loginResponse?.result?.data?._id) &&
        (!multipleHospitalsResponse?.hospitals || multipleHospitalsResponse.hospitals.length === 1)
      ) {
        payload.hospitalId =
          loginResponse?.result?.data?.hospitalId || loginResponse?.result?.data?._id;

        // 🟢 If multiple hospitals → respect user selection first
      } else if (selectedHospital) {
        payload.hospitalId = selectedHospital;

        // 🟢 If no selection → try picking a sensible default
      } else if (multipleHospitalsResponse?.hospitals?.length > 0) {
        const defaultHospital = hospitalsWithRoles.find(
          hospital =>
            hospital.userRole !== 'Radiologist' &&
            hospital.userRole !== 'Cardiologist' &&
            hospital.userRole !== 'Sonographer'
        );

        if (defaultHospital) {
          payload.hospitalId = defaultHospital._id;
        }
      }
    }

    setLoading(true);

    // The PACS token comes back from /login (dcmAccessToken); the Keycloak
    // client secret stays on the server.

    try {
      await dispatch(loginApi(payload)).unwrap();
    } catch {
      // loginApi rejection is handled by the existing loginResponse useEffect
    } finally {
      setSubmitting(false);
    }
  };

  const useDebounce = (callback, delay) => {
    const [timeoutId, setTimeoutId] = useState(null);

    const debouncedFunction = useCallback(
      (...args) => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        const id = setTimeout(() => {
          callback(...args);
        }, delay);
        setTimeoutId(id);
      },
      [callback, delay, timeoutId]
    );

    return debouncedFunction;
  };

  const debouncedHandleEmailChange = useDebounce((email: string) => {
    dispatch(multipleHospitalsApi({ email: email.toLowerCase() }));
  }, 1000);

  const handleEmailChange = (email: string) => {
    debouncedHandleEmailChange(email);
  };

  useEffect(() => {
    setLoading(false);
    if (loginResponse.status === 'SUCCESS') {
      // The password matched, but this account still needs the emailed code
      // before it's a real, completed login — show the code-entry step and
      // stop here. There is no accessToken on this response by design.
      if (loginResponse?.result?.mfaRequired) {
        setMfaChallenge({
          challengeId: loginResponse.result.challengeId,
          maskedEmail: loginResponse.result.email,
          expiresAt: loginResponse.result.expiresAt,
        });
        setOtp('');
        setOtpError('');
        return;
      }

      setDisplayMessage(loginResponse?.result?.msg);
      setShowSuccessModal(true);

      // Save token
      const token = loginResponse?.result?.accessToken;

      if (token) {
        // DICOMweb goes through the backend proxy (per-clinic scope enforced
        // server-side); the browser never holds a PACS token.
        // Save token based on the "Remember Me" toggle
        if (rememberMe) {
          localStorage.setItem('token', token);
          if (loginResponse?.result?.refreshToken) {
            localStorage.setItem('refreshToken', loginResponse.result.refreshToken);
          }
          localStorage.setItem('role', loginResponse?.result?.data?.role);
          if (loginResponse?.result?.data?.role === 'Admin') {
            localStorage.setItem('name', loginResponse?.result?.data?.adminDetails?.name);
          } else {
            localStorage.setItem('name', loginResponse?.result?.data?.name);
          }
          localStorage.setItem('userId', loginResponse?.result?.data?._id);
          localStorage.setItem(
            'hospitalId',
            loginResponse?.result?.data?.hospitalId || loginResponse?.result?.data?._id
          );
          if (loginResponse?.result?.data?.role === 'Admin') {
            localStorage.setItem('email', loginResponse?.result?.data?.adminDetails?.email);
          } else {
            localStorage.setItem('email', loginResponse?.result?.data?.email);
          }
          localStorage.setItem('loggedinEmail', loginResponse?.result?.data?.adminDetails?.email);
          localStorage.setItem('type', loginResponse?.result?.data?.type);
          if (loginResponse?.result?.data?.role === 'Admin') {
            localStorage.setItem('hospitalName', loginResponse?.result?.data?.name);
          } else {
            localStorage.setItem('hospitalName', loginResponse?.result?.data?.clinicName);
          }
          localStorage.setItem('pacsDetails', JSON.stringify(loginResponse?.result?.pacsResult));
        } else {
          sessionStorage.setItem('token', token);
          if (loginResponse?.result?.refreshToken) {
            sessionStorage.setItem('refreshToken', loginResponse.result.refreshToken);
          }
          sessionStorage.setItem('role', loginResponse?.result?.data?.role);
          if (loginResponse?.result?.data?.role === 'Admin') {
            sessionStorage.setItem('name', loginResponse?.result?.data?.adminDetails?.name);
          } else {
            sessionStorage.setItem('name', loginResponse?.result?.data?.name);
          }
          sessionStorage.setItem('userId', loginResponse?.result?.data?._id);
          sessionStorage.setItem(
            'hospitalId',
            loginResponse?.result?.data?.hospitalId || loginResponse?.result?.data?._id
          );
          if (loginResponse?.result?.data?.role === 'Admin') {
            sessionStorage.setItem('email', loginResponse?.result?.data?.adminDetails?.email);
          } else {
            sessionStorage.setItem('email', loginResponse?.result?.data?.email);
          }
          sessionStorage.setItem('loggedinEmail', loginResponse?.result?.data?.adminDetails?.email);
          sessionStorage.setItem('type', loginResponse?.result?.data?.type);
          if (loginResponse?.result?.data?.role === 'Admin') {
            if (rememberMe) {
              localStorage.setItem('hospitalName', loginResponse?.result?.data?.name);
            } else {
              sessionStorage.setItem('hospitalName', loginResponse?.result?.data?.name);
            }
          } else {
            if (rememberMe) {
              localStorage.setItem('hospitalName', loginResponse?.result?.data?.clinicName);
            } else {
              sessionStorage.setItem('hospitalName', loginResponse?.result?.data?.clinicName);
            }
          }
          sessionStorage.setItem('pacsDetails', JSON.stringify(loginResponse?.result?.pacsResult));
        }

        // Ensure pacs_access_token is set for Studies/DCM4CHEE (v2 sends dcmAccessToken above; else kcDcmTokenResponse useEffect sets it)

        // if (loginResponse?.result?.data?.pacsDetails?.[0]) {
        //   const pacs = loginResponse?.result?.data?.pacsDetails?.[0];

        //   const wadoUriRoot = `http://${pacs?.ipAddress}:${pacs?.port}/dcm4chee-arc/aets/${pacs?.aeTitle}/wado`;
        //   const qidoRoot = `http://${pacs?.ipAddress}:${pacs?.port}/dcm4chee-arc/aets/${pacs?.aeTitle}/rs`;
        //   const wadoRoot = `http://${pacs?.ipAddress}:${pacs?.port}/dcm4chee-arc/aets/${pacs?.aeTitle}/rs`;

        //   if (window?.config?.dataSources?.[0]?.configuration?.wadoUriRoot) {
        //     window.config.dataSources[0].configuration.wadoUriRoot = wadoUriRoot;
        //   }
        //   if (window?.config?.dataSources?.[0]?.configuration?.qidoRoot) {
        //     window.config.dataSources[0].configuration.qidoRoot = qidoRoot;
        //   }
        //   if (window?.config?.dataSources[0]?.configuration?.wadoRoot) {
        //     window.config.dataSources[0].configuration.wadoRoot = wadoRoot;
        //   }
        // }

        setTimeout(() => {
          setShowSuccessModal(false);
          if (fromValue === 'email') {
            navigate('/changePassword');
          }
          if (loginResponse?.result?.data?.role === 'Sonographer') {
            navigate('/');
          } else if (
            loginResponse?.result?.data?.role === 'Cardiologist' ||
            loginResponse?.result?.data?.role === 'Radiologist' ||
            loginResponse?.result?.data?.role === 'Clinician' ||
            loginResponse?.result?.data?.role === 'Medical Receptionist' ||
            loginResponse?.result?.data?.role === 'Practice Manager'
          ) {
            navigate('/reports');
          } else {
            navigate('/dashboard');
          }
        }, 500);
      } else {
        setApiRejectedReponse(loginResponse?.result?.msg);
        console.error('Token not found in login response');
      }
    }

    if (loginResponse.status === 'ERROR') {
      setOtpSubmitting(false);
      if (mfaChallenge) {
        // A wrong/expired code — stay on the code-entry step, show it inline
        // rather than the generic failed-login modal, and don't touch the
        // Turnstile widget (this failure has nothing to do with it).
        setOtpError(loginResponse?.msg || 'Invalid or expired verification code.');
        return;
      }
      setShowFailedModal(true);
      setApiRejectedReponse(loginResponse?.msg);
      // The submitted token was consumed by the failed attempt — remount the
      // widget so the next Sign In sends a fresh one.
      resetTurnstile();
    }
    // `mfaChallenge` deliberately NOT in deps: this effect only needs to run
    // when `loginResponse.status` itself changes, and at that moment it reads
    // the current render's `mfaChallenge` correctly regardless. Adding it as a
    // dependency would re-run this effect on "Back to login" (which clears
    // mfaChallenge) while `loginResponse.status` is still the stale SUCCESS
    // from the earlier mfaRequired response — re-entering the mfaRequired
    // branch and calling setMfaChallenge again, an infinite loop.
  }, [loginResponse.status]);

  const handleVerifyMfa = async () => {
    if (!mfaChallenge || otp.trim().length !== 6) return;
    setOtpSubmitting(true);
    setOtpError('');
    try {
      const result = await dispatch(
        verifyLoginMfaApi({ challengeId: mfaChallenge.challengeId, otp: otp.trim(), trustDevice })
      ).unwrap();
      // Stored so the login thunk can send it back next time — a browser this
      // device wasn't issued a token for behaves exactly as it always did.
      if (result?.deviceToken) {
        saveDeviceToken(result.deviceToken);
      }
      setMfaChallenge(null);
    } catch {
      // verifyLoginMfaApi.rejected is handled by the loginResponse effect above.
    } finally {
      setOtpSubmitting(false);
    }
  };

  const handleBackToLoginFromMfa = () => {
    setMfaChallenge(null);
    setOtp('');
    setOtpError('');
    setTrustDevice(false);
  };

  useEffect(() => {
    // The browser no longer holds a PACS token — DICOMweb goes through the backend proxy.
    // Any Keycloak DCM token that still arrives is discarded, never stored.
    dispatch(clearkcDcmTokenState());
  }, [kcDcmTokenResponse.status]);

  useEffect(() => {
    // check if the user is logged in, if not navigate to home
    if (isValidUser()) {
      navigate('/clinics');
    }
  }, []);

  return (
    <>
      <Box
        className="loginv2"
        sx={{
          minHeight: '100dvh',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
          background: 'radial-gradient(120% 100% at 0% 0%, #0e2740 0%, #03101d 55%, #020a13 100%)',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.05fr 1fr' },
            width: '100%',
            maxWidth: 960,
            overflow: 'hidden',
            borderRadius: '22px',
            backgroundColor: '#0b1726',
            border: '1px solid #243652',
            boxShadow: '0 30px 80px -30px rgba(0,0,0,.75)',
          }}
        >
          {/* Brand panel */}
          <Box
            sx={{
              position: 'relative',
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              justifyContent: 'flex-start',
              gap: 2,
              p: '40px 36px',
              color: '#e7eef7',
              overflow: 'hidden',
              background:
                'radial-gradient(130% 90% at 0% 0%, #16314e 0%, #0a1828 58%, #06101d 100%)',
            }}
          >
            {/* Product logo — same asset as the in-app header (client ask) */}
            <Box
              sx={{
                position: 'relative',
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                '& img': { height: 46, width: 'auto', display: 'block' },
              }}
            >
              {getIcon('logoWeb10')}
            </Box>
            <Box
              component="svg"
              viewBox="0 0 600 120"
              preserveAspectRatio="none"
              aria-hidden="true"
              sx={{
                position: 'relative',
                width: '100%',
                height: 92,
                mt: 1,
                mb: 0.5,
                zIndex: 1,
                opacity: 0.95,
              }}
            >
              <polyline
                points="0,60 180,60 210,60 224,28 244,96 262,60 360,60 384,44 404,80 424,60 600,60"
                fill="none"
                stroke="rgba(45,212,191,.16)"
                strokeWidth={2}
              />
              <Box
                component="polyline"
                points="0,60 180,60 210,60 224,28 244,96 262,60 360,60 384,44 404,80 424,60 600,60"
                fill="none"
                stroke="#2dd4bf"
                strokeWidth={2.5}
                sx={{
                  filter: 'drop-shadow(0 0 6px rgba(45,212,191,.6))',
                  strokeDasharray: '240 1400',
                  strokeDashoffset: 1640,
                  animation: `${ekg} 3.4s linear infinite`,
                }}
              />
            </Box>
            <Box sx={{ position: 'relative', zIndex: 2 }}>
              <Typography
                component="h2"
                sx={{
                  fontFamily: "'Spectral',serif",
                  fontSize: 30,
                  lineHeight: 1.16,
                  fontWeight: 600,
                  mb: 2.25,
                }}
              >
                Echo reporting,
                <br />
                web-delivered.
              </Typography>
              <Box
                component="ul"
                sx={{
                  listStyle: 'none',
                  p: 0,
                  m: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.25,
                }}
              >
                {[
                  'Full-resolution DICOM echo viewer with on-image measurement',
                  'Clinical measurement worksheet — auto EF, volumes, gradients & ratios',
                  'Structured reporting — templates, audit trail, finalize-to-lock',
                  'HL7 distribution to Australian referral networks (Medical Outcomes)',
                  'Multi-clinic, role-based access — fully audited',
                ].map(t => (
                  <Box
                    component="li"
                    key={t}
                    sx={{
                      position: 'relative',
                      pl: '20px',
                      color: '#9fb2cc',
                      fontSize: 13.5,
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: '7px',
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        backgroundColor: '#2dd4bf',
                        boxShadow: '0 0 8px rgba(45,212,191,.7)',
                      },
                    }}
                  >
                    {t}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          {/* Form panel */}
          <Box
            sx={{
              backgroundColor: '#0e1c2e',
              p: { xs: '32px 26px', md: '44px 40px' },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            {mfaChallenge ? (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography
                    component="h1"
                    sx={{ fontFamily: "'Spectral',serif", fontSize: 27, fontWeight: 600, color: '#e7eef7' }}
                  >
                    Check your email
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: '#6a7e9c', mt: 0.5 }}>
                    We sent a 6-digit code to {mfaChallenge.maskedEmail}. It expires at{' '}
                    {new Date(mfaChallenge.expiresAt).toLocaleTimeString()}.
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  autoFocus
                  label="Verification code"
                  placeholder="123456"
                  value={otp}
                  onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  onKeyDown={e => {
                    if (e.key === 'Enter') handleVerifyMfa();
                  }}
                  error={Boolean(otpError)}
                  helperText={otpError || ' '}
                  inputProps={{ inputMode: 'numeric', maxLength: 6, style: { letterSpacing: 6, fontSize: 20 } }}
                  sx={{ mb: 2 }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={trustDevice}
                      onChange={() => setTrustDevice(!trustDevice)}
                      sx={switchSx}
                    />
                  }
                  label="Trust this device for 30 days"
                  sx={{
                    m: 0,
                    mb: 2,
                    '& .MuiFormControlLabel-label': { color: '#9fb2cc', fontSize: 13 },
                  }}
                />

                <Button
                  fullWidth
                  variant="contained"
                  disabled={otp.length !== 6 || otpSubmitting}
                  onClick={handleVerifyMfa}
                  sx={{
                    bgcolor: '#2dd4bf',
                    color: '#04221d',
                    fontWeight: 700,
                    textTransform: 'none',
                    py: 1.2,
                    '&:hover': { bgcolor: '#25b7a4' },
                  }}
                >
                  {otpSubmitting ? (
                    <>
                      <CircularProgress
                        size={18}
                        thickness={5}
                        sx={{ color: '#04221d', mr: 1 }}
                      />
                      Verifying…
                    </>
                  ) : (
                    'Verify and sign in'
                  )}
                </Button>

                <Link
                  component="button"
                  type="button"
                  onClick={handleBackToLoginFromMfa}
                  sx={{ ...linkSx, display: 'block', mt: 2, textAlign: 'center' }}
                >
                  Back to sign in
                </Link>
              </>
            ) : (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography
                component="h1"
                sx={{
                  fontFamily: "'Spectral',serif",
                  fontSize: 27,
                  fontWeight: 600,
                  color: '#e7eef7',
                }}
              >
                Welcome back
              </Typography>
              <Typography sx={{ fontSize: 13, color: '#6a7e9c', mt: 0.5 }}>
                Sign in to continue
              </Typography>
            </Box>

            <RadioGroup
              row
              value={loginMode}
              onChange={e => {
                const v = e.target.value as 'clinician' | 'gp';
                setLoginMode(v);
                if (v === 'gp') navigate('/gp/login');
              }}
              sx={{ mb: 2.5, gap: 2 }}
            >
              <FormControlLabel
                value="clinician"
                control={
                  <Radio
                    size="small"
                    sx={radioSx}
                  />
                }
                label="Clinician"
                sx={{ m: 0, '& .MuiFormControlLabel-label': { color: '#9fb2cc', fontSize: 13 } }}
              />
              <FormControlLabel
                value="gp"
                control={
                  <Radio
                    size="small"
                    sx={radioSx}
                  />
                }
                label="Referring GP"
                sx={{ m: 0, '& .MuiFormControlLabel-label': { color: '#9fb2cc', fontSize: 13 } }}
              />
            </RadioGroup>

            <Formik
              initialValues={loginInitialValues}
              validationSchema={loginValidationSchema}
              onSubmit={submitLogin}
            >
              {({ errors, touched, values, setFieldValue }) => (
                <Form>
                  <TextField
                    fullWidth
                    type="email"
                    name="email"
                    autoComplete="off"
                    placeholder="Email address"
                    value={values.email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setFieldValue('email', e.target.value);
                      handleEmailChange(e.target.value);
                    }}
                    error={!!(touched.email && errors.email)}
                    helperText={touched.email && errors.email}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MailIcon />
                        </InputAdornment>
                      ),
                    }}
                    sx={fieldSx}
                  />

                  {(multipleHospitalsResponse?.hospitals?.length > 1 ||
                    (multipleHospitalsResponse?.hospitals?.length === 1 &&
                      (hospitalsWithRoles[0]?.userRole === 'Radiologist' ||
                        hospitalsWithRoles[0]?.userRole === 'Cardiologist' ||
                        hospitalsWithRoles[0]?.userRole === 'Sonographer' ||
                        hospitalsWithRoles[0]?.userRole === 'Practice Manager' ||
                        hospitalsWithRoles[0]?.userRole === 'Medical Receptionist'))) && (
                    <Autocomplete
                      fullWidth
                      autoHighlight
                      disabled={multipleHospitalsResponse?.hospitals?.length === 1}
                      options={hospitalsWithRoles}
                      getOptionLabel={option => capitalizeFirstLetterOfEachWord(option?.name || '')}
                      isOptionEqualToValue={(option, val) => option._id === val._id}
                      value={hospitalsWithRoles.find(h => h._id === selectedHospital) || null}
                      onChange={(_e, val) => setSelectedHospital(val?._id || '')}
                      loading={multipleHospitalsResponse?.loading}
                      noOptionsText="No clinics found"
                      sx={{
                        '& .MuiAutocomplete-popupIndicator': { color: '#6a7e9c' },
                        '& .MuiAutocomplete-clearIndicator': { color: '#6a7e9c' },
                      }}
                      slotProps={{
                        paper: {
                          sx: {
                            backgroundColor: '#0e1c2e',
                            color: '#e7eef7',
                            border: '1px solid #243652',
                            borderRadius: '12px',
                            mt: 0.5,
                          },
                        },
                        listbox: {
                          sx: {
                            '& .MuiAutocomplete-option': { fontSize: 14, py: 1 },
                            "& .MuiAutocomplete-option[aria-selected='true']": {
                              backgroundColor: 'rgba(45,212,191,.18)',
                            },
                            '& .MuiAutocomplete-option.Mui-focused': {
                              backgroundColor: 'rgba(45,212,191,.14)',
                            },
                          },
                        },
                      }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          name="hospital"
                          placeholder="Search for your clinic…"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <ClinicIcon />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                            endAdornment: (
                              <>
                                {multipleHospitalsResponse?.loading ? (
                                  <CircularProgress
                                    size={16}
                                    sx={{ color: '#6a7e9c', mr: 1 }}
                                  />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                          sx={fieldSx}
                        />
                      )}
                    />
                  )}

                  {(multipleHospitalsResponse?.hospitals?.length <= 1 || selectedHospital) && (
                    <>
                      <TextField
                        fullWidth
                        name="password"
                        autoComplete="off"
                        placeholder="Password"
                        type={showPassword ? 'text' : 'password'}
                        value={values.password}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setFieldValue('password', e.target.value)
                        }
                        error={!!(touched.password && errors.password)}
                        helperText={touched.password && errors.password}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockIcon />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                                sx={{ color: '#6a7e9c' }}
                              >
                                {showPassword ? <EyeIcon /> : <EyeOffIcon />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        sx={fieldSx}
                      />

                      {apiRejectedReponse && (
                        <Typography sx={{ color: '#fb7185', fontSize: 13, mb: 1 }}>
                          {apiRejectedReponse}
                        </Typography>
                      )}

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          my: 0.5,
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              checked={rememberMe}
                              onChange={() => setRememberMe(!rememberMe)}
                              sx={switchSx}
                            />
                          }
                          label="Remember me"
                          sx={{
                            m: 0,
                            '& .MuiFormControlLabel-label': { color: '#9fb2cc', fontSize: 13 },
                          }}
                        />
                        <Link
                          component="button"
                          type="button"
                          onClick={() =>
                            navigate('/forgotPassword', { state: { hospitalId: selectedHospital } })
                          }
                          sx={linkSx}
                        >
                          Forgot password?
                        </Link>
                      </Box>

                      {/* Cloudflare Turnstile captcha */}
                      <Box sx={{ mt: 1.5, mb: 0.5 }}>
                        <Turnstile
                          key={turnstileKey}
                          sitekey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY as string}
                          theme="dark"
                          appearance="always"
                          onSuccess={token => {
                            setTurnstileToken(token);
                            setTurnstileError(null);
                          }}
                          onError={() => {
                            // No key bump here — remounting on error would loop
                            // forever when the challenge endpoint is blocked.
                            setTurnstileToken(null);
                            setTurnstileError('Security check failed, please try again.');
                          }}
                          onExpire={() => {
                            resetTurnstile();
                            setTurnstileError('Security check expired, please retry.');
                          }}
                        />
                        {turnstileError && (
                          <Typography sx={{ color: '#fb7185', fontSize: 13, mt: 1 }}>
                            {turnstileError}
                          </Typography>
                        )}
                      </Box>

                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disableElevation
                        disabled={loading}
                        sx={btnSx}
                      >
                        {loading ? (
                          <>
                            <CircularProgress
                              size={18}
                              thickness={5}
                              sx={{ color: '#04221d', mr: 1 }}
                            />
                            Signing in…
                          </>
                        ) : (
                          'Sign In'
                        )}
                      </Button>
                    </>
                  )}
                </Form>
              )}
            </Formik>
              </>
            )}
          </Box>
        </Paper>
      </Box>
      <Failed
        handleCloseModal={() => setShowFailedModal(false)}
        title={apiRejectedReponse}
        isFailedModalOpen={showFailedModal}
      />

      {/* Bespoke on-theme signing-in / success overlay — only on actual submit/success,
          NOT during the background email→clinic lookup (that must not blur the page). */}
      <Backdrop
        open={loading || showSuccessModal}
        sx={{
          zIndex: theme => theme.zIndex.modal + 1,
          backdropFilter: 'blur(6px)',
          backgroundColor: 'rgba(3,12,22,0.72)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            px: 6,
            py: 5,
            minWidth: 260,
            borderRadius: '20px',
            backgroundColor: '#0e1c2e',
            border: '1px solid #243652',
            boxShadow: '0 30px 80px -28px rgba(0,0,0,0.85)',
            fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
          }}
        >
          {showSuccessModal ? (
            <>
              <Box
                component="svg"
                viewBox="0 0 52 52"
                sx={{ width: 72, height: 72 }}
              >
                <Box
                  component="circle"
                  cx="26"
                  cy="26"
                  r="24"
                  fill="none"
                  stroke="#2dd4bf"
                  strokeWidth="2.5"
                  sx={{
                    strokeDasharray: 151,
                    strokeDashoffset: 151,
                    animation: `${drawRing} 0.5s ease forwards`,
                  }}
                />
                <Box
                  component="path"
                  d="M16 27l7 7 13-14"
                  fill="none"
                  stroke="#2dd4bf"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  sx={{
                    strokeDasharray: 40,
                    strokeDashoffset: 40,
                    animation: `${drawCheck} 0.35s 0.4s ease forwards`,
                  }}
                />
              </Box>
              <Typography
                sx={{
                  fontFamily: "'Spectral',serif",
                  fontSize: 21,
                  fontWeight: 600,
                  color: '#e7eef7',
                }}
              >
                Welcome back
              </Typography>
              <Typography sx={{ fontSize: 13, color: '#9fb2cc', mt: -1 }}>
                {displayMessage || 'Signed in successfully'}
              </Typography>
            </>
          ) : (
            <>
              <CircularProgress
                size={48}
                thickness={4}
                sx={{ color: '#2dd4bf' }}
              />
              <Typography sx={{ fontSize: 14, color: '#9fb2cc' }}>Signing you in…</Typography>
            </>
          )}
        </Box>
      </Backdrop>
    </>
  );
};

export default Login;
